import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../Includes/Sidebar';
import Footer from '../Includes/Footer';
import { Link, useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import GeneralService from '../../services/general.service';

export default function DoctorsList() {
    const navigate = useNavigate();
    const [doctors, setDoctors] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);
    const [genderFilter, setGenderFilter] = useState('all');
    const [degreeFilter, setDegreeFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);

    // Base URL for images - make it configurable
    const BASE_IMAGE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.azmrehabcentre.pk';

    // API Call to Fetch Doctors Data
  const fetchDoctors = async () => {
    try {
        setIsLoading(true);
        const response = await GeneralService.listAllDoctors();
        console.log("API Response:", response.data);

        if (Array.isArray(response.data)) {
            // Check for missing IDs and fix them
            const doctorsWithIds = response.data.map((doctor, index) => {
                if (!doctor.id) {
                    console.warn(`Doctor at index ${index} has no ID, using index as fallback:`, doctor);
                    return { ...doctor, id: doctor.doctor_id || index + 1 }; // fallback ID
                }
                return doctor;
            });
            setDoctors(doctorsWithIds);
        } else if (response.data && response.data.doctors) {
            // Handle nested response structure
            setDoctors(response.data.doctors);
        } else {
            console.error("Unexpected response format:", response.data);
            setDoctors([]);
        }
    } catch (error) {
        console.error("Error fetching doctors:", error);
        console.error("Error details:", error.response?.data);
        setDoctors([]);
    } finally {
        setIsLoading(false);
    }
};

    // Handle delete function
    const handleDelete = async (doctorId) => {
        if (!doctorId) {
            alert("Doctor ID is missing. Cannot delete.");
            return;
        }

        try {
            await GeneralService.deleteDoctor(doctorId);
            fetchDoctors();
        } catch (error) {
            console.error("Error deleting doctor:", error);
            alert("Failed to delete doctor. Please try again.");
        }
    };

    // Handle edit navigation
    const handleEditClick = (doctorId) => {
        console.log("Edit clicked for doctor ID:", doctorId);
        
        if (!doctorId) {
            alert("Doctor ID is missing. Cannot edit.");
            return;
        }

        // Navigate programmatically for better debugging
        try {
            navigate(`/edit-doctor/${doctorId}`);
        } catch (error) {
            console.error("Navigation error:", error);
            alert("Failed to navigate to edit page.");
        }
    };

    useEffect(() => {
        fetchDoctors();
    }, []);

    const handleSearch = useCallback(
        debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 300),
        []
    );

    useEffect(() => {
        return () => handleSearch.cancel();
    }, [handleSearch]);

    // Filter doctors based on search query and filters
    const filteredDoctors = doctors.filter(doctor => 
        (doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         doctor.designation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         doctor.specialization?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         doctor.degrees?.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (genderFilter === 'all' || doctor.gender === genderFilter) &&
        (degreeFilter === 'all' || doctor.degrees?.includes(degreeFilter))
    );

    const totalRecords = filteredDoctors.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const displayedDoctors = filteredDoctors.slice(startIndex, startIndex + recordsPerPage);

    // Format date function
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get unique degrees for filter
    const uniqueDegrees = [...new Set(doctors.flatMap(doctor => 
        doctor.degrees ? doctor.degrees.split(',').map(degree => degree.trim()) : []
    ))];

    // Function to handle image loading errors
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'flex';
    };

    return (
        <div className="content">
            <Sidebar />
            <div className="container-fluid pt-4 px-4">
                <div className="bg-light text-center rounded p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0">
                            Total Doctors <span className="number-records">({totalRecords})</span>
                        </h5>
                        <div className="d-flex gap-2">
                            <select 
                                className="form-control" 
                                onChange={(e) => { 
                                    setGenderFilter(e.target.value); 
                                    setCurrentPage(1); 
                                }}
                            >
                                <option value="all">All Genders</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                            <select 
                                className="form-control" 
                                onChange={(e) => { 
                                    setDegreeFilter(e.target.value); 
                                    setCurrentPage(1); 
                                }}
                            >
                                <option value="all">All Degrees</option>
                                {uniqueDegrees.map(degree => (
                                    <option key={degree} value={degree}>{degree}</option>
                                ))}
                            </select>
                            <input 
                                type="text" 
                                placeholder="Search doctors..." 
                                className="form-control" 
                                onChange={(e) => handleSearch(e.target.value)} 
                            />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="bg-gred">
                                <tr className="text-dark">
                                    <th>No</th>
                                    <th>Image</th>
                                    <th>Name</th>
                                    <th>Designation</th>
                                    <th>Specialization</th>
                                    <th>Degrees</th>
                                    <th>Gender</th>
                                    <th>Created At</th>
                                    <th>Updated At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="10" className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayedDoctors.length > 0 ? (
                                    displayedDoctors.map((doctor, index) => (
                                        <tr key={doctor.id || index}>
                                            <td>{startIndex + index + 1}</td>
                                            <td style={{ width: "120px", position: "relative" }}>
                                                <img 
                                                    src={`${BASE_IMAGE_URL}/${doctor.image}`}
                                                    alt="Doctor Image" 
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                    onError={handleImageError}
                                                    onLoad={() => console.log("Image loaded successfully")}
                                                />
                                                <div 
                                                    style={{ 
                                                        width: '100px', 
                                                        height: '100px', 
                                                        backgroundColor: '#f8f9fa', 
                                                        border: '2px dashed #dee2e6', 
                                                        borderRadius: '8px',
                                                        display: 'none', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        fontSize: '12px', 
                                                        color: '#6c757d',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    No Image
                                                </div>
                                            </td>
                                            <td>{doctor.name || 'N/A'}</td>
                                            <td>{doctor.designation || 'N/A'}</td>
                                            <td>{doctor.specialization || 'N/A'}</td>
                                            <td>{doctor.degrees || 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${doctor.gender === 'male' ? 'bg-primary' : 'bg-info'}`}>
                                                    {doctor.gender || 'N/A'}
                                                </span>
                                            </td>
                                            <td>{doctor.created_at ? formatDate(doctor.created_at) : 'N/A'}</td>
                                            <td>{doctor.updated_at ? formatDate(doctor.updated_at) : 'N/A'}</td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {/* Debug: Show doctor ID in console and use button instead of Link for better debugging */}
                                                    <button 
                                                        className="btn btn-sm btn-warning"
                                                        title={`Edit (ID: ${doctor.id})`}
                                                        onClick={() => handleEditClick(doctor.id)}
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    
                                                    {/* Alternative: Keep the Link as backup */}
                                                    {/* Uncomment this if you prefer Link over button */}
                                                    {/*
                                                    <Link 
                                                        to={`/edit-doctor/${doctor.id}`} 
                                                        className="btn btn-sm btn-warning"
                                                        title="Edit"
                                                        onClick={(e) => {
                                                            if (!doctor.id) {
                                                                e.preventDefault();
                                                                alert("Doctor ID is missing. Cannot edit.");
                                                            }
                                                        }}
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Link>
                                                    */}
                                                    
                                                    <button 
                                                        className="btn btn-sm btn-danger"
                                                        title="Delete"
                                                        onClick={() => {
                                                            if (window.confirm('Are you sure you want to delete this doctor?')) {
                                                                handleDelete(doctor.id);
                                                            }
                                                        }}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">No doctors found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && totalPages > 1 && (
                        <div className="pagination-container mt-3">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage - 1)} 
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages).keys()].map((num) => (
                                    <li 
                                        key={num + 1} 
                                        className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}
                                    >
                                        <button 
                                            className="page-link" 
                                            onClick={() => setCurrentPage(num + 1)}
                                        >
                                            {num + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage + 1)} 
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}